import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Layers, CheckCircle, PackageCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { getUnassignedDocketsCount, assignDocketsToCustomer } from '../utils/db/bookings';
import { listCustomers } from '../utils/db/customers';
import CustomerAutocomplete from '../components/CustomerAutocomplete';

const StationarySales = () => {
  const { user } = useAuth();
  const [availableCount, setAvailableCount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  
  const selectedCustomerId = watch('customerId');

  useEffect(() => {
    if (user) {
      loadStats();
      loadCustomers();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      const count = await getUnassignedDocketsCount(user.id);
      setAvailableCount(count);
    } catch (e) {
      console.error('Failed to load docket stats', e);
    }
  };

  const loadCustomers = async () => {
    try {
      const rows = await listCustomers(user.id);
      setCustomers(rows);
    } catch (e) {
      toast.error('Failed to load customers');
    }
  };

  const generateAwbRange = (fromStr, toStr) => {
    fromStr = fromStr.trim();
    toStr = toStr.trim();

    const matchFrom = fromStr.match(/^(.*?)(\d+)(.*?)$/);
    const matchTo = toStr.match(/^(.*?)(\d+)(.*?)$/);

    if (!matchFrom || !matchTo) {
      throw new Error('AWB numbers must have a numeric part to increment.');
    }

    const prefix = matchFrom[1];
    const suffix = matchFrom[3];
    const numFrom = parseInt(matchFrom[2], 10);
    const numTo = parseInt(matchTo[2], 10);

    if (numFrom > numTo) {
      throw new Error('"From" docket number must be less than or equal to "To" docket.');
    }

    const maxCount = 500;
    if (numTo - numFrom + 1 > maxCount) {
      throw new Error(`Cannot assign more than ${maxCount} dockets at once.`);
    }

    const padLength = matchFrom[2].length;
    const awbs = [];
    for (let i = numFrom; i <= numTo; i++) {
      const numStr = i.toString().padStart(padLength, '0');
      awbs.push(`${prefix}${numStr}${suffix}`);
    }

    return awbs;
  };

  const onSubmit = async (data) => {
    if (!data.customerId) {
      toast.error('Please select a customer.');
      return;
    }

    setIsLoading(true);
    try {
      const awbsToAssign = generateAwbRange(data.docketFrom, data.docketTo);

      if (awbsToAssign.length === 0) {
        throw new Error('No dockets generated.');
      }

      if (awbsToAssign.length > availableCount) {
         toast.warning(`You are trying to assign ${awbsToAssign.length} dockets, but only ${availableCount} are available. Checking availability...`);
      }

      const result = await assignDocketsToCustomer(user.id, data.customerId, awbsToAssign);

      if (result.failed > 0) {
        toast.warning(`Assigned ${result.success} dockets, but ${result.failed} failed. See console.`);
        console.error('Failed assignments:', result.errors);
      } else {
        toast.success(`Successfully assigned ${result.success} docket(s) to customer!`);
        reset({
          docketFrom: '',
          docketTo: ''
        });
      }
      
      // Reload stats
      loadStats();
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(`Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <PackageCheck className="h-6 w-6 mr-2 text-orange-500" />
          Stationary Sales (Assignment)
        </h1>
        <p className="text-gray-600 mt-1">Assign available stationary dockets to your customers</p>
      </div>

      {/* Inventory Stats Widget */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Available Inventory</h2>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-4xl font-black text-indigo-950">{availableCount}</span>
            <span className="text-sm font-medium text-slate-400">unassigned dockets</span>
          </div>
        </div>
        <div className="h-16 w-16 bg-indigo-50 rounded-full flex items-center justify-center">
          <Layers className="h-8 w-8 text-indigo-500" />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            Assign Dockets
          </h3>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          
          <div className="bg-yellow-50 p-4 rounded-lg border border-slate-100 mb-6">
            <CustomerAutocomplete
              label="Select Customer *"
              placeholder="Type customer ID or Name"
              onChange={(c) => setValue('customerId', c.id, { shouldValidate: true })}
              value={selectedCustomerId}
              error={errors.customerId?.message}
            />
            <input type="hidden" {...register('customerId', { required: 'Please select a customer' })} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Docket From *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Layers className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  {...register('docketFrom', {
                    required: 'Starting docket is required',
                    pattern: {
                      value: /^[A-Za-z0-9]+$/,
                      message: 'Must contain only letters and numbers'
                    }
                  })}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors font-mono font-bold text-orange-700 uppercase"
                  placeholder="e.g. D330088"
                />
              </div>
              {errors.docketFrom && (
                <p className="mt-1 text-sm text-red-600">{errors.docketFrom.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Docket To *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Layers className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  {...register('docketTo', {
                    required: 'Ending docket is required',
                    pattern: {
                      value: /^[A-Za-z0-9]+$/,
                      message: 'Must contain only letters and numbers'
                    }
                  })}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors font-mono font-bold text-orange-700 uppercase"
                  placeholder="e.g. D330095"
                />
              </div>
              {errors.docketTo && (
                <p className="mt-1 text-sm text-red-600">{errors.docketTo.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-gray-100">
            <button
              type="submit"
              disabled={isLoading || availableCount === 0}
              className="flex items-center px-6 py-2.5 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Assigning...
                </div>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Assign Dockets to Customer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StationarySales;
